import React, { useState, useEffect, useRef, useCallback } from "react";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { db, functions } from "../../firebase";
import { httpsCallable } from "firebase/functions";
import { 
  Calendar, 
  CalendarX, 
  CheckCircle, 
  Users, 
  TrendingUp,
  Loader2,
  X,
  Search,
  ChevronDown
} from "lucide-react";
import * as ADMIN_CONSTANTS from "../../constants/admin";

// Helper function para formatar datas sem date-fns
const formatDate = (date: Date, formatType: 'full' | 'short'): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  if (formatType === 'full') {
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }
  return `${day}/${month}/${year.toString().slice(-2)} ${hours}:${minutes}`;
};

// Interfaces
interface AppointmentFilters {
  startDate: string;
  endDate: string;
  therapistId: string;
  patientId: string;
  status: string;
  minParticipants: string;
  maxParticipants: string;
}

interface Appointment {
  id: string;
  terapeutaId: string;
  terapeutaNome: string;
  pacienteId: string;
  pacienteNome: string;
  dataSessao: string;
  status: string;
  participantCount: number;
  isPublic?: boolean;
  permiteParticipantes?: boolean;
  duracao?: number;
}

interface AppointmentStats {
  total: number;
  confirmed: number;
  pending: number;
  cancelled: number;
  totalParticipants: number;
  avgParticipants: number;
}

interface UserOption {
  id: string;
  name: string;
  email?: string;
}

const AppointmentsOverview: React.FC = () => {
  // Estados de filtros
  const [filters, setFilters] = useState<AppointmentFilters>({
    startDate: "",
    endDate: "",
    therapistId: ADMIN_CONSTANTS.FILTER_ALL_VALUE,
    patientId: ADMIN_CONSTANTS.FILTER_ALL_VALUE,
    status: ADMIN_CONSTANTS.FILTER_ALL_VALUE,
    minParticipants: "",
    maxParticipants: "",
  });

  // Estados de dados
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [stats, setStats] = useState<AppointmentStats | null>(null);
  const [therapists, setTherapists] = useState<UserOption[]>([]);
  const [patients, setPatients] = useState<UserOption[]>([]);

  // Estados de UI
  const [isLoadingAppointments, setIsLoadingAppointments] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [isLoadingTherapists, setIsLoadingTherapists] = useState(false);
  const [isLoadingPatients, setIsLoadingPatients] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [lastDocId, setLastDocId] = useState<string | null>(null);

  // Estados do modal
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [selectedAppointmentParticipants, setSelectedAppointmentParticipants] = useState<any[]>([]);
  const [isLoadingParticipants, setIsLoadingParticipants] = useState(false);

  // Estados de dropdowns
  const [isTherapistDropdownOpen, setIsTherapistDropdownOpen] = useState(false);
  const [isPatientDropdownOpen, setIsPatientDropdownOpen] = useState(false);
  const [therapistSearchTerm, setTherapistSearchTerm] = useState("");
  const [patientSearchTerm, setPatientSearchTerm] = useState("");

  // Refs
  const observerRef = useRef<HTMLDivElement>(null);
  const therapistDropdownRef = useRef<HTMLDivElement>(null);
  const patientDropdownRef = useRef<HTMLDivElement>(null);

  // Cloud Functions
  const listAllAppointmentsFunction = httpsCallable<any, any>(functions, "listAllAppointments");
  const getAppointmentStatsFunction = httpsCallable<any, any>(functions, "getAppointmentStats");

  // Buscar terapeutas
  useEffect(() => {
    const fetchTherapists = async () => {
      setIsLoadingTherapists(true);
      try {
        const q = query(
          collection(db, "users"),
          where("role", "==", "terapeuta")
        );
        const snapshot = await getDocs(q);
        const therapistsList = snapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name || doc.data().email || "Sem nome",
          email: doc.data().email
        }));
        setTherapists(therapistsList);
      } catch (err) {
        console.error("Erro ao buscar terapeutas:", err);
      } finally {
        setIsLoadingTherapists(false);
      }
    };

    fetchTherapists();
  }, []);

  // Buscar pacientes
  useEffect(() => {
    const fetchPatients = async () => {
      setIsLoadingPatients(true);
      try {
        const q = query(collection(db, "users"));
        const snapshot = await getDocs(q);
        const patientsList = snapshot.docs
          .filter(doc => doc.data().role !== "administrador")
          .map(doc => ({
            id: doc.id,
            name: doc.data().name || doc.data().email || "Sem nome",
            email: doc.data().email
          }));
        setPatients(patientsList);
      } catch (err) {
        console.error("Erro ao buscar pacientes:", err);
      } finally {
        setIsLoadingPatients(false);
      }
    };

    fetchPatients();
  }, []);

  // Buscar estatísticas
  useEffect(() => {
    const fetchStats = async () => {
      setIsLoadingStats(true);
      try {
        const result = await getAppointmentStatsFunction({});
        if (result.data.success && result.data.stats) {
          setStats(result.data.stats);
        }
      } catch (err: any) {
        console.error("Erro ao buscar estatísticas:", err);
      } finally {
        setIsLoadingStats(false);
      }
    };

    fetchStats();
  }, []);

  // Buscar appointments
  const loadAppointments = useCallback(async (isLoadingMoreData = false) => {
    if (isLoadingMoreData) {
      setIsLoadingMore(true);
    } else {
      setIsLoadingAppointments(true);
      setAppointments([]);
      setLastDocId(null);
    }
    
    setError(null);

    try {
      const params: any = {
        limit: ADMIN_CONSTANTS.APPOINTMENTS_PAGE_SIZE,
      };

      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.therapistId !== ADMIN_CONSTANTS.FILTER_ALL_VALUE) params.therapistId = filters.therapistId;
      if (filters.patientId !== ADMIN_CONSTANTS.FILTER_ALL_VALUE) params.patientId = filters.patientId;
      if (filters.status !== ADMIN_CONSTANTS.FILTER_ALL_VALUE) params.status = filters.status;
      if (filters.minParticipants) params.minParticipants = parseInt(filters.minParticipants);
      if (filters.maxParticipants) params.maxParticipants = parseInt(filters.maxParticipants);
      if (isLoadingMoreData && lastDocId) params.lastDocId = lastDocId;

      const result = await listAllAppointmentsFunction(params);

      if (result.data.success) {
        const newAppointments = result.data.appointments || [];
        
        if (isLoadingMoreData) {
          setAppointments(prev => [...prev, ...newAppointments]);
        } else {
          setAppointments(newAppointments);
        }

        setHasMore(result.data.hasMore || false);
        setLastDocId(result.data.lastDocId || null);
      } else {
        setError(result.data.message || ADMIN_CONSTANTS.UI_MESSAGES.ERROR_LOADING);
      }
    } catch (err: any) {
      console.error("Erro ao buscar appointments:", err);
      setError(err.message || ADMIN_CONSTANTS.UI_MESSAGES.ERROR_LOADING);
    } finally {
      setIsLoadingAppointments(false);
      setIsLoadingMore(false);
    }
  }, [filters, lastDocId]);

  // Buscar appointments quando filtros mudam
  useEffect(() => {
    loadAppointments(false);
  }, [filters.startDate, filters.endDate, filters.therapistId, filters.patientId, filters.status, filters.minParticipants, filters.maxParticipants]);

  // Intersection Observer para scroll infinito
  useEffect(() => {
    if (!observerRef.current || isLoadingMore || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          loadAppointments(true);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(observerRef.current);

    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, loadAppointments]);

  // Fechar dropdowns quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (therapistDropdownRef.current && !therapistDropdownRef.current.contains(event.target as Node)) {
        setIsTherapistDropdownOpen(false);
      }
      if (patientDropdownRef.current && !patientDropdownRef.current.contains(event.target as Node)) {
        setIsPatientDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Buscar participantes do appointment selecionado
  const loadParticipants = async (appointmentId: string) => {
    setIsLoadingParticipants(true);
    try {
      const participantsRef = collection(db, "appointments", appointmentId, "participants");
      const snapshot = await getDocs(participantsRef);
      const participantsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setSelectedAppointmentParticipants(participantsList);
    } catch (err) {
      console.error("Erro ao buscar participantes:", err);
    } finally {
      setIsLoadingParticipants(false);
    }
  };

  // Abrir modal de detalhes
  const handleOpenDetails = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    loadParticipants(appointment.id);
  };

  // Fechar modal
  const handleCloseModal = () => {
    setSelectedAppointment(null);
    setSelectedAppointmentParticipants([]);
  };

  // Limpar filtros
  const handleClearFilters = () => {
    setFilters({
      startDate: "",
      endDate: "",
      therapistId: ADMIN_CONSTANTS.FILTER_ALL_VALUE,
      patientId: ADMIN_CONSTANTS.FILTER_ALL_VALUE,
      status: ADMIN_CONSTANTS.FILTER_ALL_VALUE,
      minParticipants: "",
      maxParticipants: "",
    });
    setTherapistSearchTerm("");
    setPatientSearchTerm("");
  };

  // Contar filtros ativos
  const activeFiltersCount = [
    filters.startDate,
    filters.endDate,
    filters.therapistId !== ADMIN_CONSTANTS.FILTER_ALL_VALUE,
    filters.patientId !== ADMIN_CONSTANTS.FILTER_ALL_VALUE,
    filters.status !== ADMIN_CONSTANTS.FILTER_ALL_VALUE,
    filters.minParticipants,
    filters.maxParticipants,
  ].filter(Boolean).length;

  // Filtrar terapeutas para o dropdown
  const filteredTherapists = therapists.filter(t =>
    t.name.toLowerCase().includes(therapistSearchTerm.toLowerCase()) ||
    t.email?.toLowerCase().includes(therapistSearchTerm.toLowerCase())
  );

  // Filtrar pacientes para o dropdown
  const filteredPatients = patients.filter(p =>
    p.name.toLowerCase().includes(patientSearchTerm.toLowerCase()) ||
    p.email?.toLowerCase().includes(patientSearchTerm.toLowerCase())
  );

  // Formatar data para exibição
  const formatDateDisplay = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return formatDate(date, 'full');
    } catch {
      return dateString;
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Estatísticas */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        <StatCard
          icon={Calendar}
          label={ADMIN_CONSTANTS.STATS_LABELS.TOTAL_APPOINTMENTS}
          value={stats?.total || 0}
          isLoading={isLoadingStats}
        />
        <StatCard
          icon={CheckCircle}
          label={ADMIN_CONSTANTS.STATS_LABELS.CONFIRMED_APPOINTMENTS}
          value={stats?.confirmed || 0}
          isLoading={isLoadingStats}
        />
        <StatCard
          icon={Users}
          label={ADMIN_CONSTANTS.STATS_LABELS.TOTAL_PARTICIPANTS}
          value={stats?.totalParticipants || 0}
          isLoading={isLoadingStats}
        />
        <StatCard
          icon={TrendingUp}
          label={ADMIN_CONSTANTS.STATS_LABELS.AVG_PARTICIPANTS}
          value={stats?.avgParticipants?.toFixed(1) || "0.0"}
          isLoading={isLoadingStats}
        />
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow p-3 sm:p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">Filtros</h3>
          {activeFiltersCount > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-xs sm:text-sm text-gray-600">
                {activeFiltersCount} {activeFiltersCount === 1 ? "filtro ativo" : "filtros ativos"}
              </span>
              <button
                onClick={handleClearFilters}
                className="text-xs sm:text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                {ADMIN_CONSTANTS.FILTER_LABELS.CLEAR_FILTERS}
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {/* Data Inicial */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              {ADMIN_CONSTANTS.FILTER_LABELS.START_DATE}
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Data Final */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              {ADMIN_CONSTANTS.FILTER_LABELS.END_DATE}
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              {ADMIN_CONSTANTS.FILTER_LABELS.STATUS}
            </label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={ADMIN_CONSTANTS.FILTER_ALL_VALUE}>
                {ADMIN_CONSTANTS.APPOINTMENT_STATUS_LABELS.all}
              </option>
              <option value={ADMIN_CONSTANTS.APPOINTMENT_STATUS.CONFIRMED}>
                {ADMIN_CONSTANTS.APPOINTMENT_STATUS_LABELS.confirmado}
              </option>
              <option value={ADMIN_CONSTANTS.APPOINTMENT_STATUS.PENDING}>
                {ADMIN_CONSTANTS.APPOINTMENT_STATUS_LABELS.pendente}
              </option>
              <option value={ADMIN_CONSTANTS.APPOINTMENT_STATUS.CANCELLED}>
                {ADMIN_CONSTANTS.APPOINTMENT_STATUS_LABELS.cancelado}
              </option>
            </select>
          </div>

          {/* Terapeuta Dropdown */}
          <SearchableDropdown
            label={ADMIN_CONSTANTS.FILTER_LABELS.THERAPIST}
            placeholder={ADMIN_CONSTANTS.FILTER_PLACEHOLDERS.THERAPIST}
            options={filteredTherapists}
            selectedValue={filters.therapistId}
            searchTerm={therapistSearchTerm}
            isOpen={isTherapistDropdownOpen}
            isLoading={isLoadingTherapists}
            onSearchChange={setTherapistSearchTerm}
            onToggle={() => setIsTherapistDropdownOpen(!isTherapistDropdownOpen)}
            onSelect={(id) => {
              setFilters(prev => ({ ...prev, therapistId: id }));
              setIsTherapistDropdownOpen(false);
            }}
            onClear={() => {
              setFilters(prev => ({ ...prev, therapistId: ADMIN_CONSTANTS.FILTER_ALL_VALUE }));
              setTherapistSearchTerm("");
            }}
            dropdownRef={therapistDropdownRef}
          />

          {/* Paciente Dropdown */}
          <SearchableDropdown
            label={ADMIN_CONSTANTS.FILTER_LABELS.PATIENT}
            placeholder={ADMIN_CONSTANTS.FILTER_PLACEHOLDERS.PATIENT}
            options={filteredPatients}
            selectedValue={filters.patientId}
            searchTerm={patientSearchTerm}
            isOpen={isPatientDropdownOpen}
            isLoading={isLoadingPatients}
            onSearchChange={setPatientSearchTerm}
            onToggle={() => setIsPatientDropdownOpen(!isPatientDropdownOpen)}
            onSelect={(id) => {
              setFilters(prev => ({ ...prev, patientId: id }));
              setIsPatientDropdownOpen(false);
            }}
            onClear={() => {
              setFilters(prev => ({ ...prev, patientId: ADMIN_CONSTANTS.FILTER_ALL_VALUE }));
              setPatientSearchTerm("");
            }}
            dropdownRef={patientDropdownRef}
          />

          {/* Mín Participantes */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              {ADMIN_CONSTANTS.FILTER_LABELS.MIN_PARTICIPANTS}
            </label>
            <input
              type="number"
              min="0"
              value={filters.minParticipants}
              onChange={(e) => setFilters(prev => ({ ...prev, minParticipants: e.target.value }))}
              placeholder={ADMIN_CONSTANTS.FILTER_PLACEHOLDERS.MIN_PARTICIPANTS}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Máx Participantes */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              {ADMIN_CONSTANTS.FILTER_LABELS.MAX_PARTICIPANTS}
            </label>
            <input
              type="number"
              min="0"
              value={filters.maxParticipants}
              onChange={(e) => setFilters(prev => ({ ...prev, maxParticipants: e.target.value }))}
              placeholder={ADMIN_CONSTANTS.FILTER_PLACEHOLDERS.MAX_PARTICIPANTS}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Erro */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Tabela/Cards */}
      <div className="bg-white rounded-lg shadow">
        {/* Desktop Table */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {ADMIN_CONSTANTS.TABLE_HEADERS.DATE}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {ADMIN_CONSTANTS.TABLE_HEADERS.THERAPIST}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {ADMIN_CONSTANTS.TABLE_HEADERS.PATIENT}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {ADMIN_CONSTANTS.TABLE_HEADERS.PARTICIPANTS}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {ADMIN_CONSTANTS.TABLE_HEADERS.STATUS}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {ADMIN_CONSTANTS.TABLE_HEADERS.ACTIONS}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoadingAppointments ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600" />
                    <p className="mt-2 text-sm text-gray-600">{ADMIN_CONSTANTS.UI_MESSAGES.LOADING}</p>
                  </td>
                </tr>
              ) : appointments.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12">
                    <EmptyState />
                  </td>
                </tr>
              ) : (
                appointments.map((appointment) => (
                  <tr
                    key={appointment.id}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => handleOpenDetails(appointment)}
                  >
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDateDisplay(appointment.dataSessao)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {appointment.terapeutaNome}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {appointment.pacienteNome}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {appointment.participantCount || 0}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <StatusBadge status={appointment.status} />
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenDetails(appointment);
                        }}
                        className="text-blue-600 hover:text-blue-700 font-medium"
                      >
                        {ADMIN_CONSTANTS.UI_MESSAGES.VIEW_DETAILS}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="lg:hidden divide-y divide-gray-200">
          {isLoadingAppointments ? (
            <div className="p-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600" />
              <p className="mt-2 text-sm text-gray-600">{ADMIN_CONSTANTS.UI_MESSAGES.LOADING}</p>
            </div>
          ) : appointments.length === 0 ? (
            <div className="p-4">
              <EmptyState />
            </div>
          ) : (
            appointments.map((appointment) => (
              <AppointmentCard
                key={appointment.id}
                appointment={appointment}
                onViewDetails={handleOpenDetails}
                formatDate={formatDateDisplay}
              />
            ))
          )}
        </div>

        {/* Loading More Indicator */}
        {isLoadingMore && (
          <div className="p-4 text-center border-t">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-blue-600" />
            <p className="mt-2 text-sm text-gray-600">{ADMIN_CONSTANTS.UI_MESSAGES.LOADING_MORE}</p>
          </div>
        )}

        {/* No More Results */}
        {!isLoadingAppointments && !isLoadingMore && !hasMore && appointments.length > 0 && (
          <div className="p-4 text-center border-t">
            <p className="text-sm text-gray-600">{ADMIN_CONSTANTS.UI_MESSAGES.NO_MORE_RESULTS}</p>
          </div>
        )}

        {/* Observer Element for Infinite Scroll */}
        <div ref={observerRef} className="h-4" />
      </div>

      {/* Modal de Detalhes */}
      {selectedAppointment && (
        <AppointmentDetailModal
          appointment={selectedAppointment}
          participants={selectedAppointmentParticipants}
          isLoadingParticipants={isLoadingParticipants}
          onClose={handleCloseModal}
          formatDate={formatDateDisplay}
        />
      )}
    </div>
  );
};

// Componentes auxiliares
interface StatCardProps {
  icon: React.ElementType;
  label: string;
  value: number | string;
  isLoading: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ icon: Icon, label, value, isLoading }) => (
  <div className="bg-white rounded-lg shadow p-3 sm:p-4">
    <div className="flex items-center justify-between">
      <div className="flex-1">
        <p className="text-xs sm:text-sm text-gray-600 mb-1">{label}</p>
        {isLoading ? (
          <div className="animate-pulse bg-gray-200 h-6 sm:h-8 w-16 rounded"></div>
        ) : (
          <p className="text-lg sm:text-2xl font-bold text-gray-900">{value}</p>
        )}
      </div>
      <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
    </div>
  </div>
);

interface StatusBadgeProps {
  status: string;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
  const statusLower = status?.toLowerCase() || "";
  const colorClass = ADMIN_CONSTANTS.APPOINTMENT_STATUS_COLORS[statusLower as keyof typeof ADMIN_CONSTANTS.APPOINTMENT_STATUS_COLORS] || "bg-gray-100 text-gray-800";
  const label = ADMIN_CONSTANTS.APPOINTMENT_STATUS_LABELS[statusLower as keyof typeof ADMIN_CONSTANTS.APPOINTMENT_STATUS_LABELS] || status;

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${colorClass}`}>
      {label}
    </span>
  );
};

const EmptyState: React.FC = () => (
  <div className="flex flex-col items-center justify-center py-8 sm:py-12">
    <CalendarX className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mb-3" />
    <p className="text-sm sm:text-base font-medium text-gray-900 mb-1">
      {ADMIN_CONSTANTS.UI_MESSAGES.EMPTY_APPOINTMENTS}
    </p>
    <p className="text-xs sm:text-sm text-gray-600">
      {ADMIN_CONSTANTS.UI_MESSAGES.EMPTY_APPOINTMENTS_SUBTITLE}
    </p>
  </div>
);

interface AppointmentCardProps {
  appointment: Appointment;
  onViewDetails: (appointment: Appointment) => void;
  formatDate: (date: string) => string;
}

const AppointmentCard: React.FC<AppointmentCardProps> = ({ appointment, onViewDetails, formatDate }) => (
  <div
    className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
    onClick={() => onViewDetails(appointment)}
  >
    <div className="flex items-start justify-between mb-2">
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-900 mb-1">
          {formatDate(appointment.dataSessao)}
        </p>
        <StatusBadge status={appointment.status} />
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onViewDetails(appointment);
        }}
        className="text-xs text-blue-600 hover:text-blue-700 font-medium"
      >
        {ADMIN_CONSTANTS.UI_MESSAGES.VIEW_DETAILS}
      </button>
    </div>
    <div className="space-y-1 text-sm">
      <p className="text-gray-700">
        <span className="font-medium">Terapeuta:</span> {appointment.terapeutaNome}
      </p>
      <p className="text-gray-700">
        <span className="font-medium">Paciente:</span> {appointment.pacienteNome}
      </p>
      <p className="text-gray-700">
        <span className="font-medium">Participantes:</span> {appointment.participantCount || 0}
      </p>
    </div>
  </div>
);

interface SearchableDropdownProps {
  label: string;
  placeholder: string;
  options: UserOption[];
  selectedValue: string;
  searchTerm: string;
  isOpen: boolean;
  isLoading: boolean;
  onSearchChange: (term: string) => void;
  onToggle: () => void;
  onSelect: (id: string) => void;
  onClear: () => void;
  dropdownRef: React.RefObject<HTMLDivElement | null>;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  label,
  placeholder,
  options,
  selectedValue,
  searchTerm,
  isOpen,
  isLoading,
  onSearchChange,
  onToggle,
  onSelect,
  onClear,
  dropdownRef,
}) => {
  const selectedOption = options.find(o => o.id === selectedValue);
  const displayText = selectedValue === ADMIN_CONSTANTS.FILTER_ALL_VALUE ? placeholder : selectedOption?.name || placeholder;

  return (
    <div ref={dropdownRef} className="relative">
      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
        {label}
      </label>
      <div className="relative">
        <button
          type="button"
          onClick={onToggle}
          className="w-full px-3 py-2 text-sm text-left border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white flex items-center justify-between"
        >
          <span className={selectedValue === ADMIN_CONSTANTS.FILTER_ALL_VALUE ? "text-gray-500" : "text-gray-900"}>
            {displayText}
          </span>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? "transform rotate-180" : ""}`} />
        </button>
        {selectedValue !== ADMIN_CONSTANTS.FILTER_ALL_VALUE && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClear();
            }}
            className="absolute right-8 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={ADMIN_CONSTANTS.FILTER_PLACEHOLDERS.SEARCH}
                className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              />
            </div>
          </div>
          <div className="overflow-y-auto max-h-48">
            {isLoading ? (
              <div className="p-4 text-center">
                <Loader2 className="w-4 h-4 animate-spin mx-auto text-blue-600" />
              </div>
            ) : options.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-600">
                Nenhum resultado encontrado
              </div>
            ) : (
              options.map((option) => (
                <button
                  key={option.id}
                  onClick={() => onSelect(option.id)}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 transition-colors"
                >
                  <div className="font-medium text-gray-900">{option.name}</div>
                  {option.email && (
                    <div className="text-xs text-gray-600">{option.email}</div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

interface AppointmentDetailModalProps {
  appointment: Appointment;
  participants: any[];
  isLoadingParticipants: boolean;
  onClose: () => void;
  formatDate: (date: string) => string;
}

const AppointmentDetailModal: React.FC<AppointmentDetailModalProps> = ({
  appointment,
  participants,
  isLoadingParticipants,
  onClose,
  formatDate,
}) => {
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full sm:w-11/12 md:w-4/5 lg:w-3/4 xl:w-2/3 max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-4 sm:px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">
            {ADMIN_CONSTANTS.MODAL_LABELS.TITLE}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label={ADMIN_CONSTANTS.UI_MESSAGES.CLOSE}
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 space-y-6">
          {/* Informações Básicas */}
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">
              {ADMIN_CONSTANTS.MODAL_LABELS.BASIC_INFO}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoItem label={ADMIN_CONSTANTS.MODAL_LABELS.DATE} value={formatDate(appointment.dataSessao)} />
              <InfoItem label={ADMIN_CONSTANTS.MODAL_LABELS.STATUS}>
                <StatusBadge status={appointment.status} />
              </InfoItem>
              <InfoItem label={ADMIN_CONSTANTS.MODAL_LABELS.THERAPIST} value={appointment.terapeutaNome} />
              <InfoItem label={ADMIN_CONSTANTS.MODAL_LABELS.PATIENT} value={appointment.pacienteNome} />
              <InfoItem
                label={ADMIN_CONSTANTS.MODAL_LABELS.PARTICIPANT_COUNT}
                value={appointment.participantCount?.toString() || "0"}
              />
              <InfoItem
                label={ADMIN_CONSTANTS.MODAL_LABELS.PUBLIC}
                value={appointment.isPublic ? ADMIN_CONSTANTS.MODAL_LABELS.YES : ADMIN_CONSTANTS.MODAL_LABELS.NO}
              />
            </div>
          </div>

          {/* Participantes */}
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">
              {ADMIN_CONSTANTS.MODAL_LABELS.PARTICIPANTS_INFO}
            </h3>
            {isLoadingParticipants ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                <span className="ml-2 text-sm text-gray-600">
                  {ADMIN_CONSTANTS.MODAL_LABELS.LOADING_PARTICIPANTS}
                </span>
              </div>
            ) : participants.length === 0 ? (
              <p className="text-sm text-gray-600">{ADMIN_CONSTANTS.MODAL_LABELS.NO_PARTICIPANTS}</p>
            ) : (
              <div className="space-y-2">
                {participants.map((participant) => (
                  <div
                    key={participant.id}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 font-medium text-sm">
                        {participant.userName?.charAt(0)?.toUpperCase() || "?"}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {participant.userName || "Nome não disponível"}
                      </p>
                      {participant.userEmail && (
                        <p className="text-xs text-gray-600">{participant.userEmail}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

interface InfoItemProps {
  label: string;
  value?: string;
  children?: React.ReactNode;
}

const InfoItem: React.FC<InfoItemProps> = ({ label, value, children }) => (
  <div>
    <p className="text-xs sm:text-sm text-gray-600 mb-1">{label}</p>
    {children || <p className="text-sm sm:text-base font-medium text-gray-900">{value}</p>}
  </div>
);

export default AppointmentsOverview;
